// @flow
import React, { Component } from 'react';
import { BizMetricAttributedData, StatSignifcanceGroup } from 'omega-rtc/views/trident-campaigns/metrics/utils';
import _ from 'lodash';
import { getStatsSignificanceGroup } from 'omega-rtc/views/trident-campaigns/metrics/stats';
import { Card } from 'semantic-grab-ui';
import {
  BizMetricSignificanceThreshold,
  GMV_KEY,
  RAW_KEY,
} from 'omega-rtc/constants/metrics';

type Props = {
  metrics: Array<BizMetricAttributedData>,
  attributedDataKey: string,
  selectedVertical: string,
  metricType: string,
};

// BusinessMetricsInsight ...
export default class BusinessMetricsInsight extends Component {
  props: Props;

  constructor(props: Props) {
    super(props);
    this.state = {};
  }

  // compareWithCompareGroup returns boolean: whether result is statistically significant or not
  // it will insert insight text into divArr
  compareWithCompareGroup = (
    bestRankedTmGroup: StatSignifcanceGroup,
    compareGroup: StatSignifcanceGroup,
    rankedDescTmGroupMetric: Array<StatSignifcanceGroup>,
    divArr: Array<any>,
    selectedVertical: string,
    attributedDataKey: string,
    isCompareControl: boolean,
  ) => {
    const metricsName = _.startCase(`${selectedVertical} ${attributedDataKey === GMV_KEY ? 'GMV' : attributedDataKey}`);
    const getDiffPercent = (moreVal: number, lessVal: number) => {
      return _.round(_.multiply(_.divide((moreVal - lessVal), lessVal), 100), 2);
    };
    if (bestRankedTmGroup.mean > compareGroup.mean) {
      if (isCompareControl) {
        // has control grp
        _.forEach(rankedDescTmGroupMetric, (mItem) => {
          if (mItem.mean > compareGroup.mean) {
            const diffPercent = getDiffPercent(mItem.mean, compareGroup.mean);
            divArr.push(
              <div>
                ★ <b>{mItem.name}</b> has {diffPercent}% higher {metricsName} than <b>{compareGroup.name}</b>
              </div>,
            );
          }
        });
      } else {
        const diffPercent = getDiffPercent(bestRankedTmGroup.mean, compareGroup.mean);
        divArr.push(
          <div>
            ★ {bestRankedTmGroup.name} has {diffPercent}% higher {metricsName} than {compareGroup.name}.
          </div>,
        );
      }

      const statSignificance = getStatsSignificanceGroup(bestRankedTmGroup, compareGroup);
      if (statSignificance * 100 > BizMetricSignificanceThreshold) {
        divArr.push(
          <div>
            ★ The Result is <i>statistically significant</i>! We are {BizMetricSignificanceThreshold}% confident
            that <b>{bestRankedTmGroup.name}</b> is truly better performing than <b>{compareGroup.name}</b> in terms
            of {metricsName}
          </div>,
        );
        return true;
      }

      // result not statistically significant
      divArr.push(
        <div>
          Ⓘ However, The Result is <b>NOT</b> statistically significant, we cannot conclude whether {isCompareControl ? 'the treatment groups are' : `${bestRankedTmGroup.name} is`} truly better performing than {compareGroup.name}
        </div>,
      );
      return false;
    } else if (isCompareControl) {
      divArr.push(
        <div>
          {`ⓘ  No treatment group performed better than ${compareGroup.name}`}
        </div>,
      );
      return false;
    }

    return false;
  }

  render() {
    const { attributedDataKey, selectedVertical, metrics, metricType } = this.props;

    const noInsightView = (reason: string) => (
      <Card
        style={{
          backgroundColor: '#eeee',
          padding: 10,
          width: '80%',
        }}
      >
        No insight available {reason}
      </Card>
    );

    const attributePlusVertKey: string = `${attributedDataKey}.${selectedVertical}`;
    let controlGroup: StatSignifcanceGroup;
    const rankedDescTmGroupMetric: Array<StatSignifcanceGroup> = _.reverse(_.sortBy(_.remove(_.map(metrics, (val) => {
      const statsGroup = {
        name: _.get(val, 'name', 0),
        mean: _.get(val, `data.${attributePlusVertKey}.average`, 0),
        variance: _.get(val, `data.${attributePlusVertKey}.var`, 0),
        count: _.get(val, 'distinctUserCount', 0),
      };
      if (statsGroup.count) {
        // only count valid groups
        if (val.isControlGroup) {
          controlGroup = statsGroup;
        } else {
          return statsGroup;
        }
      }
    }), el => !!el), ['mean']));

    if (_.isEmpty(rankedDescTmGroupMetric)) {
      // no treatment group to compare
      return null;
    }


    const divArr = [];
    const bestRankedTmGroup: StatSignifcanceGroup = rankedDescTmGroupMetric[0];
    let isResultSignificant = false;
    if (controlGroup) {
      // hasControlGroup
      isResultSignificant = this.compareWithCompareGroup(bestRankedTmGroup, controlGroup, rankedDescTmGroupMetric, divArr, selectedVertical, attributedDataKey, true);
    } else {
      if (metricType === RAW_KEY) {
        // as no control group available for RAW metrics
        return null;
      }

      // metricsType is MAM
      if (rankedDescTmGroupMetric.length < 2) {
        // only one treatment group
        // as only one treatment group available for MAM metrics
        return null;
      }

      const worstPerformGroup: StatSignifcanceGroup = rankedDescTmGroupMetric[rankedDescTmGroupMetric.length - 1];
      if (bestRankedTmGroup.mean <= worstPerformGroup.mean) {
        // as all treatment groups perform same
        return noInsightView('as all treatment groups perform same');
      }
      isResultSignificant = this.compareWithCompareGroup(bestRankedTmGroup, worstPerformGroup, rankedDescTmGroupMetric, divArr, selectedVertical, attributedDataKey, false);
    }

    if (_.isEmpty(divArr)) {
      return null;
    }

    return (
      <Card
        style={{
          backgroundColor: isResultSignificant ? '#BFE2FD' : '#FBF5DF',
          padding: 10,
          width: '80%',
        }}
      >
        {_.map(divArr, el => el)}
      </Card>
    );
  }
}
